import { useDebouncedState, useLocalStorage } from '@mantine/hooks';
import { createColumnHelper } from '@tanstack/react-table';
import { ReactNode, useCallback, useMemo } from 'react';
import { LuChevronDown, LuChevronRight, LuMail, LuMessageSquare, LuPhone, LuRefreshCw } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { FeatureFlag } from '@ndvr/auth-api';
import { PhoneCallView, ZoomPhoneCallState } from '@ndvr/customer-api';

import { MultiFilter, MultiFilterSection } from '@/components/MultiFilter';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/utils/cn';
import { formatDate, formatTime, useHouseholdRoutePaths, useHouseholdRouteType, useRequiredParams } from 'utils';
import { useFeatureFlag } from 'views/auth/useFeatureFlag';
import { CALL_STATE_META, callCounterpartyName, formatCallDuration } from 'views/household/HouseholdCalls/call-utils';
import { HouseholdHeaderActionsPortal } from 'views/household/HouseholdIndividualPage';
import { CustomTablePage, SearchInput } from 'widgets';

import { useMessages, useSyncMessages } from './message-hooks';
import { MessageRow } from './message-types';
import { filterMessages, MessageTypeFilter, party } from './message-utils';

const TYPE_FILTERS: { value: MessageTypeFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'email', label: 'Email' },
    { value: 'text', label: 'Text' },
    { value: 'call', label: 'Call' },
];

const HOUSEHOLD_MESSAGES_FILTER_KEY = 'household-messages-filter';
const HOUSEHOLD_MESSAGES_CALL_STATES_KEY = 'household-messages-call-states';
const columnHelper = createColumnHelper<MessageRow>();

const PartyCell = ({ name, detail }: { name: string; detail: string }) => {
    const bothPresent = name && detail && name !== detail;
    if (bothPresent) {
        return (
            <div className="flex flex-col">
                <span className="text-sm">{name}</span>
                <span className="text-xs text-gray-400">{detail}</span>
            </div>
        );
    }
    return <span className="text-sm">{name || detail}</span>;
};

const CallStatusFilterContent = ({
    selected,
    onToggle,
}: {
    selected: ZoomPhoneCallState[];
    onToggle: (state: ZoomPhoneCallState) => void;
}) => (
    <div className="space-y-2">
        {Object.values(ZoomPhoneCallState).map((state) => (
            <div
                key={state}
                className="flex cursor-pointer items-center space-x-3 rounded p-2 hover:bg-gray-50"
                onClick={() => onToggle(state)}
            >
                <Checkbox checked={selected.includes(state)} />
                <span className="text-sm">{CALL_STATE_META[state].label}</span>
            </div>
        ))}
    </div>
);

/** The call row's badges — one base style, per-badge colors from the call palette. */
const CallBadge = ({ className, children }: { className: string; children: ReactNode }) => (
    <Badge
        variant="outline"
        className={cn('flex-shrink-0 border-transparent text-[11px] tracking-wide uppercase', className)}
    >
        {children}
    </Badge>
);

const CallCell = ({ call }: { call: PhoneCallView }) => {
    const stateMeta = CALL_STATE_META[call.state];
    return (
        <div className="flex min-w-0 flex-col gap-0.5 overflow-hidden">
            <div className="flex min-w-0 items-center gap-2.5">
                <span className="truncate font-medium">{callCounterpartyName(call)}</span>
                <CallBadge className={stateMeta.badgeClassName}>{stateMeta.label}</CallBadge>
                {/* Talk time is neutral metadata, so it borrows the palette's neutral pair. A missed call has none. */}
                {!!call.durationSeconds && (
                    <CallBadge className="bg-[#F8F7F5] text-[#716664]">
                        {formatCallDuration(call.durationSeconds)}
                    </CallBadge>
                )}
                {call.hasNote && <CallBadge className="bg-[#FFF6EB] text-[#7D552B]">Note</CallBadge>}
            </div>
            {call.noteSummary && <span className="text-muted-foreground truncate text-xs">{call.noteSummary}</span>}
        </div>
    );
};

export const HouseholdMessagesTab = () => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const navigate = useNavigate();
    const { TextsThreadRoutePath, CallDetailRoutePath } = useHouseholdRoutePaths();
    const { baseRoute } = useHouseholdRouteType();

    const callsEnabled = useFeatureFlag(FeatureFlag.CC_PHONE_CALLS);
    const { messages, isLoading } = useMessages(globalAccountGUID);
    const syncMutation = useSyncMessages(globalAccountGUID);
    const [filter, setFilter] = useLocalStorage<MessageTypeFilter>({
        key: HOUSEHOLD_MESSAGES_FILTER_KEY,
        defaultValue: 'all',
    });
    const [callStates, setCallStates] = useLocalStorage<ZoomPhoneCallState[]>({
        key: HOUSEHOLD_MESSAGES_CALL_STATES_KEY,
        defaultValue: [],
    });

    const typeOptions = callsEnabled ? TYPE_FILTERS : TYPE_FILTERS.filter((option) => option.value !== 'call');

    const toggleCallState = (state: ZoomPhoneCallState) =>
        setCallStates((selected) =>
            selected.includes(state) ? selected.filter((s) => s !== state) : [...selected, state],
        );

    const filterSections: MultiFilterSection<'type' | 'callStatus'>[] = [
        {
            id: 'type',
            label: 'Type',
            count: filter !== 'all' ? 1 : 0,
            onClear: () => setFilter('all'),
            content: (
                <div className="space-y-1">
                    {typeOptions.map((option) => (
                        <button
                            key={option.value}
                            onClick={() => setFilter(option.value)}
                            className={`flex w-full rounded px-2 py-1.5 text-left text-sm ${
                                filter === option.value
                                    ? 'bg-blue-50 font-medium text-blue-700'
                                    : 'text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            ),
        },
        ...(callsEnabled
            ? [
                  {
                      id: 'callStatus' as const,
                      label: 'Call status',
                      count: callStates.length,
                      onClear: () => setCallStates([]),
                      content: <CallStatusFilterContent selected={callStates} onToggle={toggleCallState} />,
                  },
              ]
            : []),
    ];

    const [searchTerm, setSearchTerm] = useDebouncedState('', 500);

    const filtered = useMemo(
        // With calls off, saved statuses would hide every row with no section left to clear them from.
        () => filterMessages(messages, { type: filter, callStates: callsEnabled ? callStates : [], searchTerm }),
        [messages, filter, callStates, callsEnabled, searchTerm],
    );

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: 'type',
                header: 'Type',
                size: 48,
                cell: ({ row }) =>
                    row.original.type === 'email' ? (
                        <LuMail size={16} className="text-muted-foreground" />
                    ) : row.original.type === 'call' ? (
                        <LuPhone size={16} className="text-muted-foreground" />
                    ) : (
                        <LuMessageSquare size={16} className="text-muted-foreground" />
                    ),
            }),
            columnHelper.display({
                id: 'expander',
                header: '',
                size: 32,
                cell: ({ row }) =>
                    row.getCanExpand() ? (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground h-6 w-6"
                            onClick={(e) => {
                                e.stopPropagation();
                                row.toggleExpanded();
                            }}
                        >
                            {row.getIsExpanded() ? <LuChevronDown size={16} /> : <LuChevronRight size={16} />}
                        </Button>
                    ) : null,
            }),
            columnHelper.display({
                id: 'message',
                header: callsEnabled ? 'Message/Call' : 'Message',
                cell: ({ row }) => {
                    const msg = row.original;
                    return (
                        <div className="min-w-0 overflow-hidden">
                            {msg.type === 'email' ? (
                                <div className="flex w-full min-w-0 flex-col items-baseline gap-1">
                                    <span className="w-full truncate font-medium">{msg.subject || '(No Subject)'}</span>
                                    {msg.children && msg.children.length > 0 && (
                                        <span className="text-muted-foreground text-xs font-normal">
                                            {msg.children.length + 1} messages
                                        </span>
                                    )}
                                </div>
                            ) : msg.type === 'call' ? (
                                <CallCell call={msg.call} />
                            ) : (
                                <div className="truncate text-sm">{msg.preview}</div>
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.display({
                id: 'from',
                header: 'From',
                size: 200,
                cell: ({ row }) => <PartyCell {...party(row.original, 'from')} />,
            }),
            columnHelper.display({
                id: 'to',
                header: 'To',
                size: 200,
                cell: ({ row }) => <PartyCell {...party(row.original, 'to')} />,
            }),
            columnHelper.accessor('mostRecentAt', {
                header: 'Date',
                size: 130,
                cell: ({ row }) => (
                    <div className="flex flex-col text-sm">
                        <span>{formatDate(row.original.mostRecentAt)}</span>
                        <span className="text-xs text-gray-400">{formatTime(row.original.mostRecentAt)}</span>
                    </div>
                ),
            }),
        ],
        [callsEnabled],
    );

    const getSubRows = useCallback((row: MessageRow) => (row.type === 'email' ? row.children : undefined), []);

    return (
        <div>
            <HouseholdHeaderActionsPortal>
                <div className="flex items-center gap-4">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => syncMutation.mutate()}
                        disabled={syncMutation.isPending}
                    >
                        <LuRefreshCw className={syncMutation.isPending ? 'animate-spin' : ''} size={16} />
                    </Button>
                    <SearchInput
                        round
                        placeholder={callsEnabled ? 'Search...' : 'Search messages...'}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <MultiFilter sections={filterSections} defaultSectionId="type" dropdownClassName="w-84 p-0" />
                </div>
            </HouseholdHeaderActionsPortal>

            <CustomTablePage
                data={filtered}
                columns={columns}
                isLoading={isLoading}
                rowOnClick={(row) => {
                    if (row.type === 'email') {
                        navigate(`/${baseRoute}/${globalAccountGUID}/emails/${row.emailGUID}`);
                    } else if (row.type === 'call') {
                        navigate(CallDetailRoutePath(globalAccountGUID, row.call.zoomPhoneCallGUID));
                    } else {
                        navigate(TextsThreadRoutePath(globalAccountGUID, row.smsMessageGUID));
                    }
                }}
                exportName="messages"
                cardClassName="m-0 border-0 shadow-none"
                defaultSortCol="mostRecentAt"
                defaultSortDirection="desc"
                getSubRows={getSubRows}
            />
        </div>
    );
};
